/**
 * Container Activity Timeline Component
 *
 * Displays a chronological timeline of all activities related to a container/expedition.
 * Uses the shared Timeline component (central line, dots, alternating left/right cards, date labels).
 * Each card is expandable — click to reveal full details, issue context, and inline actions.
 *
 * Supports two views:
 * - Timeline: full chronological list with infinite scroll
 * - Focus: two-column triage view (Action needed / Requires review)
 */

import { useMemo, useState } from 'react';
import { useContainerActivitiesPaginated } from '@/hooks/api/use-activities';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Clock, CheckCircle, LayersThree01 } from '@untitledui/icons';
import { deduplicateBackendActivities } from '@/utils/deduplicate-activities';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Timeline, type TimelineEntry } from '@/components/application/timeline';
import { ExpandableActivityCard } from './timeline/expandable-activity-card';
import { TimelineFocusView } from './timeline/timeline-focus-view';
import { useInfiniteScroll } from './use-infinite-scroll';

interface ContainerActivityTimelineProps {
    containerId: string | undefined;
    limit?: number;
    className?: string;
}

type ViewMode = 'timeline' | 'focus';

export function ContainerActivityTimeline({
    containerId,
    limit: _limit,
    className = '',
}: ContainerActivityTimelineProps) {
    const { t } = useTranslation('pages:containers');
    const [viewMode, setViewMode] = useState<ViewMode>('timeline');
    const [showAllOccurrences, setShowAllOccurrences] = useState(false);

    const {
        activities: rawActivities,
        total,
        isLoading,
        isLoadingMore,
        error,
        hasMore,
        loadMore,
    } = useContainerActivitiesPaginated(containerId);

    const { loadMoreRef } = useInfiniteScroll({
        hasMore,
        loadMore,
    });

    // Count attention items matching visible badge logic:
    // - Issue cards: "Action needed" when requires_attention (regardless of is_read)
    // - Non-issue cards: "Requires attention" only when unread
    const attentionCount = useMemo(() => {
        return rawActivities.filter(a => {
            if (!a.requires_attention) return false;
            if (a.entity_type === 'container_issue') return true;
            return !a.is_read;
        }).length;
    }, [rawActivities]);

    // Deduplicated activities for timeline and focus views
    const visibleActivities = useMemo(() => {
        const sorted = [...rawActivities].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        if (showAllOccurrences) return sorted;
        return deduplicateBackendActivities(sorted).map(d => d.item);
    }, [rawActivities, showAllOccurrences]);

    const timelineEntries = useMemo((): TimelineEntry[] => {
        let position: 'left' | 'right' = 'left';
        return visibleActivities.map((activity) => {
            const entry: TimelineEntry = {
                id: activity.activity_id,
                date: new Date(activity.created_at),
                content: <ExpandableActivityCard activity={activity} containerId={containerId || ''} />,
                position,
            };
            position = position === 'left' ? 'right' : 'left';
            return entry;
        });
    }, [visibleActivities, containerId]);

    if (isLoading) {
        return (
            <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${className}`}>
                <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.timeline.title')}</h2>
                <div className="flex items-center justify-center py-12">
                    <p className="text-tertiary">{t('detail.loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${className}`}>
                <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.timeline.title')}</h2>
                <div className="flex items-center justify-center py-12 text-error-600">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    <span>Failed to load activities</span>
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${className}`}>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-primary">{t('detail.timeline.title')}</h2>
                <div className="flex items-center gap-3">
                    {/* View toggle */}
                    <div className="flex items-center gap-1 rounded-lg bg-secondary p-1 ring-1 ring-secondary_alt">
                        <Button
                            size="sm"
                            color={viewMode === 'timeline' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('timeline')}
                            iconLeading={Clock}
                        >
                            Timeline
                        </Button>
                        <Button
                            size="sm"
                            color={viewMode === 'focus' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('focus')}
                            iconLeading={AlertCircle}
                        >
                            Focus
                        </Button>
                    </div>
                    <Button
                        size="sm"
                        color={showAllOccurrences ? 'primary' : 'secondary'}
                        onClick={() => setShowAllOccurrences(v => !v)}
                        iconLeading={LayersThree01}
                    >
                        {showAllOccurrences ? 'Deduplica' : 'Mostra tutto'}
                    </Button>
                    {attentionCount > 0 && (
                        <Badge color="warning" size="sm">
                            {attentionCount} need attention
                        </Badge>
                    )}
                    <span className="text-xs text-tertiary">
                        {visibleActivities.length} {visibleActivities.length === 1 ? 'activity' : 'activities'}
                    </span>
                </div>
            </div>

            {rawActivities.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Clock className="h-12 w-12 text-tertiary mb-3" />
                    <p className="text-sm text-tertiary">No activities yet</p>
                    <p className="text-xs text-tertiary mt-1">
                        Activities will appear here as events occur
                    </p>
                </div>
            ) : viewMode === 'focus' ? (
                <TimelineFocusView
                    activities={visibleActivities}
                    containerId={containerId || ''}
                />
            ) : (
                <>
                    <Timeline items={timelineEntries} order="desc" />

                    {/* Load more sentinel */}
                    <div ref={loadMoreRef} className="py-2" />

                    {isLoadingMore && (
                        <div className="flex items-center justify-center py-4">
                            <p className="text-sm text-tertiary">Loading more...</p>
                        </div>
                    )}

                    {!hasMore && rawActivities.length > 0 && (
                        <div className="flex items-center justify-center gap-2 py-4">
                            <CheckCircle className="h-4 w-4 text-tertiary" />
                            <p className="text-xs text-tertiary">
                                All {total} activities loaded
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
